import { Job, RetryableJob } from "~/components/build/Show/lib/types";
import { QueryableJobsCollection } from "~/components/build/Show/lib/jobs";
import { filter, isNumber, matchesProperty } from "lodash";

/**
 * Filter step jobs to the retry lane for a specific job.
 *
 * Parallel and matrix steps have multiple independent retry chains under the
 * same step, so callers need to narrow to the reference job's lane before
 * walking or displaying attempts.
 */
export function filterRetriesForJob(stepJobs: RetryableJob[], job: RetryableJob): RetryableJob[] {
  let jobs = stepJobs;

  if ("matrix" in job && job.matrix) {
    jobs = filter(jobs, matchesProperty("matrix", job.matrix));
  }

  if ("parallelGroupIndex" in job && isNumber(job.parallelGroupIndex)) {
    jobs = filter(jobs, { parallelGroupIndex: job.parallelGroupIndex });
  }

  return jobs;
}

/**
 * All attempts for a job, in retry-chain order (oldest first) and including
 * the latest attempt.
 *
 * `stepJobs` is the full set of jobs loaded for a step. For parallel/matrix
 * steps `filterRetriesForJob` narrows the set to the lane the job belongs to.
 *
 * The latest attempt is identified by walking the `retriedInJobUuid` chain (the
 * tail has no successor), not by array position — the loaded jobs are not
 * reliably ordered, so trusting their order would surface the latest job as the
 * first "attempt" row.
 */
export function retryAttemptsForJob(
  stepJobs: RetryableJob[],
  referenceJob: RetryableJob,
): RetryableJob[] {
  const lane = filterRetriesForJob(stepJobs, referenceJob);
  if (lane.length <= 1) {
    return [];
  }

  // The tail of the chain (no `retriedInJobUuid`) is the current attempt.
  const latest = lane.find((job) => !job.retriedInJobUuid) ?? lane[lane.length - 1];
  const priorAttempts = new QueryableJobsCollection(lane as Job[]).getPriorJobsInRetryChain(
    latest as Job,
  ) as RetryableJob[];

  return [...priorAttempts, latest];
}

/**
 * The past (superseded) attempts for a job, in retry-chain order (oldest first)
 * and excluding the latest attempt — which is the row the history hangs off.
 */
export function pastAttemptsForJob(
  stepJobs: RetryableJob[],
  referenceJob: RetryableJob,
): RetryableJob[] {
  return retryAttemptsForJob(stepJobs, referenceJob).slice(0, -1);
}
